import { useEffect, useState } from "react";
import {
  doc,
  DocumentReference,
  DocumentSnapshot,
  onSnapshot,
  SnapshotOptions,
} from "firebase/firestore";
import deepequal from "fast-deep-equal";

import { useFirestore } from "../../contexts/firebase/FirebaseContext";

/**
 * Creates and caches a document reference from a
 * path or a `firestore.DocumentReference` object.
 *
 * @param path A path or a reference to a document.
 * @returns `ref`
 */
export const useDocumentReference = <T>(
  path?: string | DocumentReference<T> | null
): DocumentReference<T> | undefined => {
  const store = useFirestore();
  const [ref, setRef] = useState<DocumentReference<T>>();

  // Create reference
  useEffect(() => {
    if (!store) return;

    // Reset the ref if the path becomes empty
    if (!path) {
      setRef(undefined);
      return;
    }

    // Create new reference if path is different
    if (typeof path === "string") {
      if (path !== ref?.path) setRef(doc(store, path) as DocumentReference<T>);
    } else if (path.path !== ref?.path) {
      setRef(path);
    }
  }, [store, path, ref, setRef]);

  return ref;
};

/**
 * Retrieve and listen to a document from Firestore
 * using a path or a document reference. A listener
 * is created only when a valid path or reference is
 * given.
 *
 * Returns the following in an array:
 *  - The document snapshot when retrieved successfully. `undefined` otherwise.
 *  - A boolean representing the loading state of the document snapshot.
 *  - Error if an error occurred. `undefined` otherwise.
 *
 * @param path A path or a reference to a document.
 * @returns `[snapshot, loading, error]`
 */
export const useDocument = <T>(
  path?: string | DocumentReference<T> | null
): [DocumentSnapshot<T> | undefined, boolean, Error | undefined] => {
  const ref = useDocumentReference(path);

  const defaultLoading = Boolean(path);
  const [loading, setLoading] = useState(defaultLoading);
  const [error, setError] = useState<Error>();
  const [snapshot, setSnapshot] = useState<DocumentSnapshot<T>>();

  // Listen for document snapshots
  useEffect(() => {
    // If there is no ref, reset data
    if (!ref) {
      setLoading(defaultLoading);
      setSnapshot(undefined);
      setError(undefined);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const unsubscribe = onSnapshot(
      ref,
      (snapshot) => {
        if (cancelled) return;
        setSnapshot(snapshot);
        setLoading(false);
        setError(undefined);
      },
      (error) => {
        if (cancelled) return;
        setLoading(false);
        setError(error);
      }
    );

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, [ref, defaultLoading, setLoading, setSnapshot, setError]);

  return [snapshot, loading, error];
};

/**
 * Retrieve and listen to a document's data from Firestore.
 * A listener is created only when a valid path or reference
 * is given.
 *
 * Returns the following in an array:
 *  - The document data. `undefined` otherwise.
 *  - A boolean representing the loading state of the document snapshot.
 *  - Error if an error occurred. `undefined` otherwise.
 *
 * @param path A path or a reference to a document.
 * @param options Snapshot options.
 * @returns `[data, loading, error]`
 */
export const useDocumentData = <T>(
  path?: string | DocumentReference<T> | null,
  options?: SnapshotOptions
): [T | undefined, boolean, Error | undefined] => {
  const [snapshot, loading, error] = useDocument(path);
  const [ops, setOps] = useState(options);
  const [data, setData] = useState<T>();

  // Update options if they change
  useEffect(() => {
    if (!deepequal(options, ops)) setOps(options);
  }, [options, ops, setOps]);

  // If the snapshot changes, extract the data
  useEffect(() => {
    setData(snapshot?.data(ops));
  }, [setData, snapshot, ops]);

  return [data, loading, error];
};
